import $ from 'jquery';
import { withViewContext } from '../ui/view-context.js';

// CONCEPT: the page's one POST whs/get_inventory_and_pricing, adopted from <head> when it was
//          already sent from there (#1845).
//
//          App\Libraries\EarlyPricing prints an inline fetch at the top of the storefront layout's
//          <head> that sends the pricing request for the parts the server just rendered, about a
//          second earlier on a cold load than this module graph could. It publishes
//          window.__paceEarlyPricing = { kind, body, promise }. requestPricing() takes it - once -
//          and uses its answer instead of asking again, but ONLY when it is the request this call
//          would have made: same kind, and a body byte-identical to what $.ajax sends for
//          `data: { parts }`. Anything else - no early request, a different batch, an early
//          request that failed - falls back to the $.ajax call the page always made, so the worst
//          case is the old behaviour plus one wasted request, never a price for the wrong parts.
//
//          The one failure that does NOT fall back is HTTP 410: the server refused the customer
//          view the page belongs to (ViewAsExceptionHandler), every request from this page is
//          refused the same way, and ui/view-context.js forbids re-sending into an ended view.
//          The caller's error handler receives it as a jqXHR-shaped object, so
//          viewContextRefusal() reads it exactly as it reads a jQuery failure.
//
//          The view-as context: the early request's url is built server-side by view_as_url(),
//          the PHP twin of withViewContext(); the fallback carries it through withViewContext()
//          and the ajaxPrefilter as before.
//
// USE:     requestPricing('browse' | 'checkout', parts, { success, error, complete })
//          -> a jQuery promise that settles after complete() has run, like a jqXHR's.

export const PRICING_ENDPOINT = 'whs/get_inventory_and_pricing';
export const EARLY_PRICING_GLOBAL = '__paceEarlyPricing';

/**
 * The most parts product-browse.js puts in one 'browse' request (#2331). A page with more cards
 * sends them in several requests of this many, in page order.
 *
 * Whs::get_inventory_and_pricing() refuses more than 500 parts in one request
 * (Whs::$max_inventory_parts), and an item page whose part sits in hundreds of kits carries a
 * card for every one of them - 597 on one live page, every card left with an error. Well under
 * the cap, so the first answer, which holds the page's own part, also comes back sooner.
 *
 * App\Libraries\EarlyPricing::BROWSE_REQUEST_PARTS is the same number: the <head> request is
 * the FIRST of these requests, and it is only adopted when its body is that request's exactly.
 */
export const BROWSE_REQUEST_PARTS = 100;

/**
 * The request body $.ajax sends for `type: 'post', data: { parts }`: jQuery.param(), then the
 * %20 -> '+' rewrite jQuery applies to a form-encoded string body.
 *
 * @param {Array<object>} parts
 * @returns {string}
 */
export function pricingRequestBody(parts) {
    return $.param({ parts: parts }).replace(/%20/g, '+');
}

/**
 * The early request's promise if it is the one for (kind, parts), else null. Consumes the
 * global either way: it is good for exactly one adoption.
 *
 * @param {string} kind
 * @param {Array<object>} parts
 * @returns {Promise<string>|null}
 */
export function takeEarlyPricing(kind, parts) {
    var early = window[EARLY_PRICING_GLOBAL];

    if (!early) {
        return null;
    }

    window[EARLY_PRICING_GLOBAL] = null;

    if (
        early.kind !== kind ||
        typeof early.body !== 'string' ||
        !early.promise ||
        typeof early.promise.then !== 'function' ||
        early.body !== pricingRequestBody(parts)
    ) {
        return null;
    }

    return early.promise;
}

/**
 * The endpoint url for one kind, carrying the display-cache opt-in for 'browse' (#1848).
 *
 * ON THE URL rather than in the body, because takeEarlyPricing() adopts the <head> request only
 * when its body is byte-identical to what $.ajax sends - a field added to `data` here would have
 * to be mirrored exactly in EarlyPricing::body(), and a drift costs a silent extra request.
 *
 * 'checkout' is the cart, and the cart is always answered live.
 *
 * @param {string} kind
 * @returns {string}
 */
export function pricingUrl(kind) {
    var url = withViewContext(PRICING_ENDPOINT);

    if (kind !== 'browse') {
        return url;
    }

    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'cacheable=1';
}

function sendPricing(kind, parts, handlers) {
    return $.ajax({
        // withViewContext(): availability and PRICING are answered for the acting user, so a
        // request that lost the customer view would quietly quote the staff member's prices.
        url: pricingUrl(kind),
        type: 'post',
        dataType: 'json',
        data: { parts: parts },
        success: handlers.success,
        error: handlers.error,
        complete: handlers.complete,
    });
}

function call(fn) {
    if (typeof fn === 'function') {
        fn.apply(null, Array.prototype.slice.call(arguments, 1));
    }
}

/**
 * Price `parts`, adopting the <head> request when it is this one.
 *
 * @param {string} kind 'browse' (product-browse.js) or 'checkout' (checkout-inventory.js)
 * @param {Array<object>} parts the batch, as buildInventoryBatchEntry() built it
 * @param {{success?: Function, error?: Function, complete?: Function}} handlers $.ajax's
 * @returns {object} a jQuery promise
 */
export function requestPricing(kind, parts, handlers) {
    var early = takeEarlyPricing(kind, parts);

    if (!early) {
        return sendPricing(kind, parts, handlers);
    }

    var deferred = $.Deferred();
    var fallback = function () {
        sendPricing(kind, parts, handlers).then(deferred.resolve, deferred.reject);
    };

    early.then(
        function (text) {
            var data;

            try {
                data = JSON.parse(text);
            } catch {
                // jQuery's json converter would have failed here too; ask again the old way,
                // whose error handler then reports it exactly as it always did.
                fallback();
                return;
            }

            try {
                call(handlers.success, data);
            } finally {
                call(handlers.complete);
            }
            deferred.resolve(data);
        },
        function (failure) {
            if (failure && failure.status === 410) {
                var jqXHRLike = {
                    status: 410,
                    responseText: typeof failure.responseText === 'string' ? failure.responseText : '',
                };

                try {
                    call(handlers.error, jqXHRLike, 'error', 'Gone');
                } finally {
                    call(handlers.complete);
                }
                deferred.reject(jqXHRLike);
                return;
            }

            fallback();
        }
    );

    return deferred.promise();
}
